// src/components/LoginForm.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from '../redux/auth/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [formValues, setFormValues] = useState({ email: '', password: '' });
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [animateForm, setAnimateForm] = useState(false);
  const [formError, setFormError] = useState(null);

  const { loading, error, isAuthenticated } = useSelector((state) => state.auth);

  // Debug the error from Redux state
  useEffect(() => {
    if (error) {
      console.log("Error in LoginForm:", error);
      setFormError(error);
    } else {
      setFormError(null);
    }
  }, [error]);

  useEffect(() => {
    setTimeout(() => setAnimateForm(true), 100);
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    
    // Clear any existing errors when component mounts
    dispatch(clearError());
    
    // Clear refresh token errors on mount - these shouldn't be shown on login page
    if (error && error.includes('Refresh token expired')) {
      dispatch(clearError());
    }
    
    // Clear errors when component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [isAuthenticated, navigate, dispatch, error]);

  const handleChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (error || formError) {
      dispatch(clearError());
      setFormError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const resultAction = await dispatch(login(formValues));
      if (login.rejected.match(resultAction)) {
        // Handle the rejected action
        console.log("Login failed with error:", resultAction.payload);
        setFormError(resultAction.payload || "Authentication failed");
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      // Error is already handled in Redux state
      console.error('Login failed:', err);
      setFormError(err.message || "Authentication failed");
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden items-center justify-center lg:flex lg:w-1/2 bg-black relative overflow-hidden">
        {/* ... your decorative panel code ... */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
          <div className="text-5xl font-bold mb-6 text-white">
            Mena<span className="text-green-400">Devs</span> AI
          </div>
        </div>
      </div>

      <div
        className={`flex-1 flex flex-col justify-center items-center px-6 py-12 bg-white transition-all duration-700 ${
          animateForm ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="sm:mx-auto items-center justify-center w-full max-w-md">
          <div className="lg:hidden text-3xl font-bold text-center mb-10 text-gray-900">
            Mena<span className="text-green-500">Devs</span> AI
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-8 space-y-6">
            <h2 className="text-center text-2xl font-bold leading-9 pb-2 text-gray-900">
              Welcome Back
            </h2>
            <p className="text-center text-gray-500 text-sm">
              Sign in to access your dashboard
            </p>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-1">
                <div
                  className={`relative group transition-all duration-300 ${
                    isEmailFocused || formValues.email
                      ? 'border-green-500'
                      : 'border-gray-300'
                  } border rounded-lg p-1`}
                >
                  <label
                    htmlFor="email"
                    className={`absolute text-xs font-medium transition-all duration-300 ${
                      isEmailFocused || formValues.email
                        ? '-top-2 left-2 bg-white px-1 text-green-600'
                        : 'top-3 left-10 text-gray-500'
                    }`}
                  >
                    Email address
                  </label>
                  <div className="flex items-center">
                    <Mail
                      size={18}
                      className={`ml-2 transition-colors duration-300 ${
                        isEmailFocused || formValues.email
                          ? 'text-green-600'
                          : 'text-gray-400'
                      }`}
                    />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formValues.email}
                      onChange={handleChange}
                      onFocus={() => setIsEmailFocused(true)}
                      onBlur={() => setIsEmailFocused(false)}
                      className="block w-full ml-2 bg-transparent border-0 p-2 text-gray-900 placeholder-gray-400 focus:ring-0 focus:outline-none sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <div
                  className={`relative group transition-all duration-300 ${
                    isPasswordFocused || formValues.password
                      ? 'border-green-500'
                      : 'border-gray-300'
                  } border rounded-lg p-1`}
                >
                  <label
                    htmlFor="password"
                    className={`absolute text-xs font-medium transition-all duration-300 ${
                      isPasswordFocused || formValues.password
                        ? '-top-2 left-2 bg-white px-1 text-green-600'
                        : 'top-3 left-10 text-gray-500'
                    }`}
                  >
                    Password
                  </label>
                  <div className="flex items-center">
                    <Lock
                      size={18}
                      className={`ml-2 transition-colors duration-300 ${
                        isPasswordFocused || formValues.password
                          ? 'text-green-600'
                          : 'text-gray-400'
                      }`}
                    />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formValues.password}
                      onChange={handleChange}
                      onFocus={() => setIsPasswordFocused(true)}
                      onBlur={() => setIsPasswordFocused(false)}
                      className="block w-full ml-2 bg-transparent border-0 p-2 text-gray-900 placeholder-gray-400 focus:ring-0 focus:outline-none sm:text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="p-1 text-gray-400 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Display form error or redux error */}
              {(formError || error) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{formError || error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 px-4 py-3 text-sm font-medium text-white shadow-lg shadow-green-500/20 transition-all duration-300 hover:shadow-xl hover:shadow-green-500/30 focus:outline-none"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2" />
                    Authenticating...
                  </div>
                ) : (
                  <div className="flex items-center">
                    Sign in
                    <ArrowRight size={16} className="ml-2" />
                  </div>
                )}
              </button>

              <div className="text-center mt-4">
                <Link
                  to="/"
                  className="text-sm text-gray-500 hover:text-green-600 transition-colors duration-300 inline-flex items-center"
                >
                  <ArrowRight size={16} className="mr-1 rotate-180" />
                  Back to Home
                </Link>
              </div>
            </form>

            {/* ... extra 3rd-party login buttons, etc. ... */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;